import LGDefaultConfig from './default/config.json'
import LGDefaultLabel from './default/label.json'
import defaultConfig from './config'
import { LGProjects, LGFileNameEnum, LGOssPathEnum, LGPlatformEnum } from './constant'
import { getLGConfigs, getLGLabelsByPlatform } from './request'
import i18nInstance from './i18n'
import { cloneDeep } from 'lodash'

class LocalStorage {
  constructor(platform) {
    this.LGLocalStorageKey = `LGSdk_${platform}`
  }

  set(value) {
    if (value && typeof value === 'object') {
      localStorage.setItem(this.LGLocalStorageKey, JSON.stringify(value))
    }
  }

  get() {
    const value = localStorage.getItem(this.LGLocalStorageKey)
    if (value) {
      return JSON.parse(value)
    }
    return null
  }
}

class Observer {
  constructor() {
    this.subscribers = []
  }

  subscribe(callback) {
    this.subscribers.push(callback)
  }

  unsubscribe(callback) {
    this.subscribers = this.subscribers.filter((subscriber) => subscriber !== callback)
  }

  notify(data) {
    this.subscribers.forEach((subscriber) => subscriber(data))
  }
}

class LGConfigManager {
  constructor(config) {
    this.locale = null
    this.customLocale = null
    this.deleteLocale = null
    this.platform = null
    this.customLabels = null
    this.LGConfig = LGDefaultConfig
    this.LGLabel = LGDefaultLabel
    this.LGAliOssBaseURL = ''
    this.languageTypeList = []
    this.languageTypeKeys = []
    this.LGProjects = LGProjects
    this.LGPlatformEnum = LGPlatformEnum
    this.LGFileNameEnum = LGFileNameEnum
    this.LGOssPathEnum = LGOssPathEnum
    this.LGConfigObserver = new Observer()
    this.LGLabelObserver = new Observer()
    this.initConfig(config)
  }

  /**
   * 初始化
   * @param {*} config
   */
  initConfig(config) {
    console.log('LGSdk---多语言版本初始化', config)
    console.log('LGSdk---多语言版本初始化-打包配置', this.LGConfig)
    this.locale = config.locale || null
    this.platform = config.platform || null
    this.customLocale = config.customLocale || null
    this.deleteLocale = config.deleteLocale || null
    this.customLabels = config.customLabels || null
    this.LGAliOssBaseURL = process.env.VUE_APP_LG_ALI_OSS_BASE_URL || ''
    this.LGLocalStorage = new LocalStorage(this.platform)
    // 判断本地是否拥有多语言配置，本地启动和线上更新使用
    const localConfig = this.LGLocalStorage.get()
    if (localConfig) {
      // 判断打包配置和本地配置是否一致
      if (this.platform && localConfig.LGConfig[`${this.platform}_VERSION`] >= this.LGConfig[`${this.platform}_VERSION`]) {
        this.LGConfig = localConfig.LGConfig || LGDefaultConfig
        this.LGLabel = localConfig.LGLabel || LGDefaultLabel
      } else {
        // 更新本地配置
        this.LGLocalStorage.set({ LGConfig: this.LGConfig, LGLabel: this.LGLabel })
      }
      console.log('LGSdk---本地多语言配置', this.LGConfig, this.LGLabel)
    }
    // 初始化多语言配置
    this.setLGConfig(['config', 'label'])
  }

  /**
   * 更新多语言配置
   * @param {*} types
   */
  setLGConfig(types) {
    // 初始化配置
    if (types.includes('config') && this.LGConfig.codes) {
      this.languageTypeKeys = []
      this.languageTypeList = []
      const languageTypeListMaps = {}
      this.LGConfig.codes.forEach((item) => {
        // 过滤删除的多语言标识
        if (!this.deleteLocale || !this.deleteLocale.includes(item.code)) {
          let code = item.code
          if (this.customLocale && this.customLocale[item.code]) {
            // 替换多语言标识
            code = this.customLocale[item.code]
          }
          if (languageTypeListMaps[code]) {
            // 合并 browserCodes
            languageTypeListMaps[code].browserCodes += `,${item.browserCodes}`
          } else {
            // 添加新项
            languageTypeListMaps[code] = {
              value: code,
              label: item.name,
              browserCodes: item.browserCodes
            }
          }
        }
      })
      // 转换为数组
      this.languageTypeList = Object.values(languageTypeListMaps)
      console.log('LGSdk---多语言配置项', this.languageTypeList)
      // 多语言标识
      this.languageTypeKeys = Object.keys(languageTypeListMaps)

      if (this.locale) {
        // 设置多语言标识
        i18nInstance.setLocale(this.getBrowserLocale())
      }
      // 通知订阅者
      this.LGConfigObserver.notify(this.LGConfig)
    }
    // 设置多语言配置
    if (types.includes('label')) {
      if (this.locale) {
        // 设置多语言文本
        this.setMessage()
      }
      this.LGLabelObserver.notify(this.LGLabel)
    }
  }

  /**
   * 检查多语言配置
   */
  async checkConfig() {
    try {
      // 获取多语言配置
      const oldCofing = cloneDeep(this.LGConfig)
      const configRes = await getLGConfigs()
      const newLGConfig = configRes.data || {}
      // 版本判断
      if (newLGConfig.VERSION > this.LGConfig.VERSION) {
        console.log('LGSdk---更新多语言配置', configRes.data)
        this.LGConfig = newLGConfig
        this.setLGConfig(['config'])
      }
      // 判断当前项目文本版本
      console.log('LGSdk---多语言文本版本', this.platform, newLGConfig[`${this.platform}_VERSION`], oldCofing[`${this.platform}_VERSION`])
      if (this.platform && newLGConfig[`${this.platform}_VERSION`] > oldCofing[`${this.platform}_VERSION`]) {
        const labelsRes = await getLGLabelsByPlatform(this.platform)
        console.log('LGSdk---更新多语言文本', labelsRes.data)
        this.LGLabel = labelsRes.data || []
        // 保存到本地,并刷新页面
        this.LGLocalStorage.set({ LGConfig: newLGConfig, LGLabel: this.LGLabel })
        // 刷新页面
        window.location.reload()
        // this.setLGConfig(['label'])
      }
    } catch (error) {
      console.error('LGSdk---校验出错', error)
    }
  }

  /**
   * 订阅多语言配置
   * @param {*} callback
   */
  subscribeToConfig(callback) {
    this.LGConfigObserver.subscribe(callback)
  }

  /**
   * 取消订阅多语言配置
   * @param {*} callback
   */
  unsubscribeFromConfig(callback) {
    this.LGConfigObserver.unsubscribe(callback)
  }

  /**
   * 订阅多语言标识
   * @param {*} callback
   */
  subscribeToLabel(callback) {
    this.LGLabelObserver.subscribe(callback)
  }

  /**
   * 取消订阅多语言标识
   * @param {*} callback
   */
  unsubscribeFromLabel(callback) {
    this.LGLabelObserver.unsubscribe(callback)
  }

  /**
   * 获取多语言标识列表
   * @returns
   */
  getLGTypeList() {
    return this.languageTypeList
  }

  pickKeys(obj, keys) {
    return keys.reduce((result, key) => {
      result[key] = obj[key] || ''
      return result
    }, {})
  }

  /**
   * 通过多语言文本keys获取多语言文本内容
   * @param {*} keys 文本keys
   * @returns
   */
  getLGLabelByKeys(keys) {
    const labels = []
    this.LGLabel.forEach((item) => {
      if (keys.includes(item.keys)) {
        const lgLabel = this.pickKeys(item, this.languageTypeKeys)
        labels.push(lgLabel)
      }
    })
    return labels
  }

  /**
   * 浏览器多语言标识替换
   * @returns
   */
  getBrowserLocale() {
    let lgTag = 'en'
    for (const item of this.languageTypeList) {
      const lgKeys = item.browserCodes.split(',')
      if (lgKeys.includes(this.locale)) {
        lgTag = item.value
        break
      }
    }
    document.documentElement.lang = lgTag
    return lgTag
  }

  /**
   * 将对象的键和值进行转换，将值相同的键合并到一个数组中
   * @param {Object} obj - 输入对象
   * @returns {Object} 转换后的对象
   */
  transformObject(obj) {
    const result = {}

    for (const [key, value] of Object.entries(obj)) {
      if (!result[value]) {
        result[value] = []
      }
      result[value].push(key)
    }

    return result
  }

  /**
   * 将数组格式的多语言文本转换为对象格式
   * @param {Array} labels - 多语言文本数组
   * @param {Array} keys - 语言类型键数组
   * @returns {Object} 转换后的多语言文本对象
   */
  transformLabels(labels, keys) {
    const result = {}

    keys.forEach((key) => {
      result[key] = {}
    })

    // 获取源多语言标识
    const originalKeyMaps = this.transformObject(this.customLocale)

    labels.forEach((item) => {
      keys.forEach((key) => {
        if (item[key]) {
          // 如果有对应的多语言文本，则使用对应的多语言文本
          result[key][item.keys] = item[key]
        } else if (originalKeyMaps[key] && item[originalKeyMaps[key][0]]) {
          // 如果没有对应的多语言文本，则使用源多语言文本
          result[key][item.keys] = item[originalKeyMaps[key][0]]
        } else {
          result[key][item.keys] = ''
        }
      })
    })

    return result
  }

  /**
   * 获取多语言文本
   * @param {*} key key值
   * @param {*} data 填充数据
   * @returns
   */
  t(key, data) {
    return i18nInstance.t(key, data)
  }

  /**
   * 设置多语言文本
   */
  setMessage() {
    const lgLabelMaps = this.transformLabels(this.LGLabel, this.languageTypeKeys)
    Object.keys(lgLabelMaps).forEach((key) => {
      // 判断是否需要插入自定义文本
      if (this.customLabels && this.customLabels[key]) {
        lgLabelMaps[key] = { ...lgLabelMaps[key], ...this.customLabels[key] }
      } else if (this.customLabels && this.customLabels.others) {
        lgLabelMaps[key] = { ...lgLabelMaps[key], ...this.customLabels.others }
      }
      i18nInstance.setMessage(key, lgLabelMaps[key])
    })
    // console.log('LGSdk---多语言文本-测试1', this.t('public.wait'))
    // console.log('LGSdk---多语言文本-测试2', this.t('public.cancel', { count: '123' }))
    // i18nInstance.setLocale('en')
    // console.log('LGSdk---多语言文本-测试11', this.t('public.wait'))
    // console.log('LGSdk---多语言文本-测试22', this.t('public.cancel', { count: '123' }))
  }

  /**
   * vue挂载
   * @param {*} app vue实例
   * @param {*} config 默认配置
   * {
   *   env: 环境标识,
   *   platform: 平台标识,
   *   deleteLocale: [删除多语言标识],
   *   customLocale: { // 语言标识自定义替换
   *     'zh-Hans': 'zh',
   *     zh-Hant': 'zh'
   *   }
   * }
   */
  install(app) {
    if (this.locale) {
      // 注册国际化插件
      app.use(i18nInstance)
    }
    lgConfigManager.checkConfig()
    app.config.globalProperties.$LGSdk = this
  }
}
const lgConfigManager = new LGConfigManager(defaultConfig)
export default lgConfigManager
